/**
 * BuriedVerse e02_rapture "Rapture" — FULL overlay composition.
 * Generated by bv_overlay_gen.py. fps-SAFE (S(sec) reads useVideoConfig().fps) so the SAME comp is:
 *   • buriedverse-e02-overlays      @16fps → the 720p 0.5x preview (baked logo)
 *   • buriedverse-e02-overlays-4k   @24fps → the Topaz 4K base, logo via ffmpeg (showLogo=false)
 * Devotional route (== bible_decoder W01): overlay_design_spec route=devotional; build_overlay_windows.py
 * EXTRACTS every `start: X, out: Y` NUMERIC LITERAL below → factory_4k_full renders those clusters @scale=3.
 * Anchors are REAL times from productions.word_timestamps_hindi. 28 events (12 BvFactTag + 16 BvHero).
 */
import {
  AbsoluteFill, Img, Sequence, staticFile, useCurrentFrame, useVideoConfig, interpolate,
} from "remotion";
import { BvFactTag, BvHero } from "./BuriedVerseOverlayDemo";

export const e02_rapture_FPS = 16;
export const e02_rapture_DURATION_FRAMES = 23827;
export const e02_rapture_FPS_4K = 24;
export const e02_rapture_DURATION_FRAMES_4K = 35741;

const BvLogo: React.FC = () => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 36], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <Img src={staticFile("buried_verse_diorama_ko.png")}
      style={{ position: "absolute", top: 20, right: 20, width: 70, height: "auto", opacity: fadeIn * 0.85 }} />
  );
};

const FadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  const o = interpolate(frame, [dur - 14, dur], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

type Tag = { start: number; out: number; kicker: string; value: string };
type Hero = {
  start: number; out: number; variant: "word" | "myth" | "quote"; kicker: string;
  word?: string; translit?: string; gloss?: string; headline?: string; attribution?: string;
};

// ── SLOT 1 · fact tags (BvFactTag: citation / place / author) ──
const TAGS: Tag[] = [
  // verb harpazō gets rendered into Latin as rapiemur, "we shall be
  { start: 162.229, out: 166.829, kicker: "Latin", value: "rapiemur, from the verb rapio" },
  // young church in the port city of Thessalonica, and they have
  { start: 258.718, out: 263.318, kicker: "Place", value: "Thessalonica" },
  // he tells them. First Thessalonians four, sixteen and seventeen: "For the Lord
  { start: 309.428, out: 314.028, kicker: "Citation", value: "1 Thessalonians 4:16–17" },
  // same noun in Acts twenty-eight, verse fifteen, when the Roman
  { start: 515.5, out: 520.1, kicker: "Citation", value: "Acts 28:15, the same word" },
  // an interesting direction. In Acts chapter eight, Philip has just
  { start: 556.896, out: 561.496, kicker: "Citation", value: "Acts 8 · Philip caught away" },
  // down the coast. In Second Corinthians twelve, Paul describes a
  { start: 579.536, out: 584.136, kicker: "Citation", value: "2 Corinthians 12 · caught up to the third heaven" },
  // out of it. In Revelation twelve, a newborn child
  { start: 597.624, out: 602.224, kicker: "Citation", value: "Revelation 12 · a child snatched to safety" },
  // and promoted by an Anglo-Irish preacher named John Nelson Darby. Darby was a
  { start: 873.212, out: 877.812, kicker: "Author", value: "John Nelson Darby · 1830s" },
  // a study Bible, the Scofield Reference Bible, revised in nineteen-seventeen,
  { start: 922.984, out: 927.584, kicker: "Source", value: "Scofield Reference Bible · 1909" },
  // it for yourself. In the book of Daniel, chapter nine, there is a
  { start: 1049.04, out: 1053.64, kicker: "Citation", value: "Daniel 9 · the seventy weeks" },
  // book of Revelation: the forty-two months, the twelve hundred
  { start: 1072.022, out: 1076.622, kicker: "The math", value: "42 months · 1,260 days · “time, times, half a time”" },
  // the text seriously. There is the pre-tribulation view, that the church
  { start: 1153.526, out: 1158.126, kicker: "Competing maps", value: "Pre-trib · mid-trib · pre-wrath · post-trib" },
];

// ── SLOT 2 · hero cards (BvHero: word / myth / quote) ──
const HEROES: Hero[] = [
  // the strange thing: the word "rapture" is not in it. What the verse
  { start: 7.837, out: 14.837, variant: "myth", kicker: "The myth", headline: "“Rapture” is not the word in the verse. The verse says “caught up.”" },
  // "caught up" is the Greek verb harpazō. It is a
  { start: 127.619, out: 134.819, variant: "word", kicker: "The word", word: "ἁρπάζω", translit: "harpazō", gloss: "To seize, snatch, carry off by force. The word for a bird of prey, or a thief." },
  // will tell you we should not simply pin it on Jerome. The point survives
  { start: 191.834, out: 198.834, variant: "myth", kicker: "The honest bit", headline: "Often pinned on Jerome. The reviser of Paul’s letters is actually anonymous." },
  // word Paul wrote. It is a later Latin coat of paint, and the paint
  { start: 232.244, out: 239.244, variant: "myth", kicker: "The turn", headline: "The word is a later Latin coat of paint. And the paint has a date." },
  // who are left, will be caught up together with them in the clouds
  { start: 327.302, out: 334.502, variant: "quote", kicker: "The text", headline: "“Caught up together with them, to meet the Lord in the air.”", attribution: "1 Thessalonians 4:17" },
  // a grieving church, your dead are not last, they are first.
  { start: 350.672, out: 357.672, variant: "myth", kicker: "The comfort", headline: "The dead rise first. Nobody is left out of the reunion." },
  // quiet at all. There is a shout. There is the
  { start: 382.549, out: 389.549, variant: "myth", kicker: "The myth", headline: "The scene is not silent. There is a shout, an archangel’s voice, a trumpet." },
  // meet the Lord." The Greek noun there is apantēsis. Ordinarily, in plain
  { start: 426.534, out: 433.734, variant: "word", kicker: "The word", word: "ἀπάντησις", translit: "apantēsis", gloss: "A meeting. In antiquity, a city’s welcome party sent out to escort an arriving ruler." },
  // whole scene is a welcome party, not a departure. It is a
  { start: 478.71, out: 485.71, variant: "myth", kicker: "The reading", headline: "Some hear a welcome party, not a departure: the church goes out to meet the Lord, then walks back with him." },
  // his followers that no one will "snatch" them out of his hand, the same violent
  { start: 616.061, out: 623.261, variant: "quote", kicker: "The reversal", headline: "“No one will snatch them out of my hand.” The same violent verb, this time a promise.", attribution: "John 10:28" },
  // of those passages. Not one of them comes with a timetable. The verb tells
  { start: 660.851, out: 667.851, variant: "myth", kicker: "The catch", headline: "The verb tells you the manner: sudden, seized, carried. Never the year or the sequence." },
  // in the field; one will be taken and the other left. Two women will
  { start: 696.238, out: 703.438, variant: "quote", kicker: "The text", headline: "“One will be taken and the other left.”", attribution: "Matthew 24:40" },
  // flood came and TOOK THEM ALL AWAY." Sit with that.
  { start: 747.941, out: 754.941, variant: "myth", kicker: "The frame", headline: "In Noah’s flood, the ones “taken away” were swept off in the judgment. Noah was the one left." },
  // framework is recent does not, by itself, make it wrong. Plenty of true
  { start: 967.643, out: 974.643, variant: "myth", kicker: "In fairness", headline: "A framework being recent doesn’t make it wrong. A late birthday is not a lie." },
  // hangs on, is never actually stated as such anywhere in the
  { start: 1029.47, out: 1036.47, variant: "myth", kicker: "The turn", headline: "“Seven years” is never stated in the New Testament. The number is constructed, not quoted." },
  // shouting match does. Separate the three layers and hold each
  { start: 1398.916, out: 1405.916, variant: "myth", kicker: "The task", headline: "Separate the word, the verse, and the timeline. Hold each one for what it is." },
];

export const BuriedVerseE02RaptureOverlays: React.FC<{ showLogo?: boolean }> = ({ showLogo = true }) => {
  const { fps } = useVideoConfig();
  const S = (sec: number) => Math.round(sec * fps);
  return (
    <AbsoluteFill>
      {showLogo && <BvLogo />}
      {TAGS.map((t, i) => (
        <Sequence key={`tag-${i}`} from={S(t.start)} durationInFrames={S(t.out - t.start)}>
          <FadeOut dur={S(t.out - t.start)}>
            <BvFactTag kicker={t.kicker} value={t.value} />
          </FadeOut>
        </Sequence>
      ))}
      {HEROES.map((h, i) => (
        <Sequence key={`hero-${i}`} from={S(h.start)} durationInFrames={S(h.out - h.start)}>
          <FadeOut dur={S(h.out - h.start)}>
            <BvHero variant={h.variant} kicker={h.kicker} word={h.word} translit={h.translit}
              gloss={h.gloss} headline={h.headline} attribution={h.attribution} />
          </FadeOut>
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
